import React, { useRef, useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Menu, MenuItem, Select, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LineChart } from "@mui/x-charts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useLazyGetVendorLeadTrendsQuery } from "store/services/reportApi";

const CARD_HEIGHT = 410;
const SUB_CHART_HEIGHT = 125;

// ── Stat Chip ─────────────────────────────────────────────────────────────────
const StatChip = ({ label, value, paletteKey = "error", shade = "main" }) => (
    <Box
        sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette[paletteKey][shade], 0.1),
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
        }}
    >
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: `${paletteKey}.${shade}`, lineHeight: 1 }}>
            {value}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1, whiteSpace: "nowrap" }}>
            {label}
        </Typography>
    </Box>
);

const VendorLeadTrendChart = () => {
    const chartRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const theme = useTheme();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [chartWidth, setChartWidth] = useState(600);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const menuTimeout = useRef(null);
    const [months, setMonths] = useState(6);
    const containerRef = useCallback((node) => {
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
            resizeObserverRef.current = null;
        }
        if (node) {
            const observer = new ResizeObserver((entries) => {
                const width = entries[0]?.contentRect?.width;
                if (width) setChartWidth(width);
            });
            observer.observe(node);
            resizeObserverRef.current = observer;
        }
    }, []);
    const [triggerGetVendorLeadTrends, { data: response = {}, isLoading, isError }] =
        useLazyGetVendorLeadTrendsQuery();
    useEffect(() => {
        triggerGetVendorLeadTrends({ months });
    }, [months, triggerGetVendorLeadTrends]);
    const trendData = response.data || [];
    const summary = response.summary || { totalCreated: 0, totalWon: 0, conversionRate: 0 };
    const labels = trendData.map((entry) => entry.label);
    const totalCreated = trendData.map((entry) => entry.created);
    const totalWon = trendData.map((entry) => entry.won);
    const years = trendData.map((entry) => entry.year);
    const yearRange =
        years.length > 0
            ? years[0] === years[years.length - 1]
                ? `${years[0]}`
                : `${years[0]} – ${years[years.length - 1]}`
            : "";
    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
        clearTimeout(menuTimeout.current);
        menuTimeout.current = setTimeout(() => setAnchorEl(null), 3000);
    };
    const handleMenuClose = () => {
        clearTimeout(menuTimeout.current);
        setAnchorEl(null);
    };
    useEffect(() => {
        if (open) {
            clearTimeout(menuTimeout.current);
            menuTimeout.current = setTimeout(() => setAnchorEl(null), 5000);
        }
        return () => clearTimeout(menuTimeout.current);
    }, [open]);
    const handleFullScreen = () => {
        if (chartRef.current?.requestFullscreen) chartRef.current.requestFullscreen();
        setIsFullScreen(true);
        handleMenuClose();
    };
    const handleMinimize = () => {
        document.exitFullscreen();
        setIsFullScreen(false);
    };
    useEffect(() => {
        const exitHandler = () => {
            if (!document.fullscreenElement) setIsFullScreen(false);
        };
        document.addEventListener("fullscreenchange", exitHandler);
        return () => document.removeEventListener("fullscreenchange", exitHandler);
    }, []);
    const handleDownload = async (format) => {
        if (!chartRef.current) return;
        const canvas = await html2canvas(chartRef.current, {
            backgroundColor: getComputedStyle(chartRef.current).backgroundColor,
            scale: 2,
            useCORS: true,
        });
        const image = canvas.toDataURL("image/png");
        if (format === "pdf") {
            const pdf = new jsPDF("landscape", "pt", "a4");
            pdf.addImage(image, "PNG", 0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight());
            pdf.save("vendor_lead_trend_chart.pdf");
        } else {
            const link = document.createElement("a");
            link.href = image;
            link.download = `vendor_lead_trend_chart.${format}`;
            link.click();
        }
        handleMenuClose();
    };
    if (isLoading) return <div>Loading chart...</div>;
    if (isError) return <div>Failed to load data</div>;
    const subChartHeight = isFullScreen ? 300 : SUB_CHART_HEIGHT;
    const commonXAxis = [{ scaleType: "point", data: labels }];
    const createdMax = Math.max(...totalCreated, 1);
    const createdTickStep = Math.ceil(createdMax / 3);
    const createdYAxis = [{ min: 0, tickMinStep: createdTickStep }];
    const wonYAxis = [{ min: 0, tickMinStep: 1 }];
    const commonSx = {
        "& .MuiChartsAxis-tickLabel": { fill: theme.palette.text.secondary },
        "& .MuiChartsAxis-line": { stroke: theme.palette.divider },
        "& .MuiChartsAxis-tick": { stroke: theme.palette.divider },
    };
    return (
        <Box
            ref={chartRef}
            sx={{
                width: isFullScreen ? "100vw" : "100%",
                height: isFullScreen ? "100vh" : CARD_HEIGHT,
                minHeight: isFullScreen ? "100vh" : CARD_HEIGHT,
                padding: 2,
                boxShadow: 2,
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="baseline" gap={1}>
                    <Typography
                        variant="p"
                        sx={{ fontWeight: "bold", color: theme.palette.primary.main }}
                    >
                        Lead Trends (Last {months} Months)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {yearRange}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    <Select
                        value={months}
                        onChange={(e) => setMonths(Number(e.target.value))}
                        size="small"
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value={6}>Last 6 Months</MenuItem>
                        <MenuItem value={12}>Last 12 Months</MenuItem>
                    </Select>
                    {isFullScreen && (
                        <IconButton onClick={handleMinimize}>
                            <FullscreenExitIcon />
                        </IconButton>
                    )}
                    <IconButton onClick={handleMenuClick}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                        <MenuItem onClick={handleFullScreen}>View Full Screen</MenuItem>
                        <MenuItem onClick={() => handleDownload("png")}>
                            <GetAppIcon sx={{ mr: 1 }} /> Download PNG
                        </MenuItem>
                        <MenuItem onClick={() => handleDownload("jpeg")}>
                            <GetAppIcon sx={{ mr: 1 }} /> Download JPEG
                        </MenuItem>
                        <MenuItem onClick={() => handleDownload("pdf")}>
                            <PictureAsPdfIcon sx={{ mr: 1 }} /> Download PDF
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            {/* Stat chip row */}
            <Box display="flex" gap={1} mb={1.5}>
                <StatChip label="Total Created" value={summary.totalCreated} paletteKey="error" />
                <StatChip label="Total Won" value={summary.totalWon} paletteKey="success" />
            </Box>

            {/* Charts */}
            <Box ref={containerRef} display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="caption" color="error.main" fontWeight={600} pl={1}>
                    Lead Created
                </Typography>
                <LineChart
                    xAxis={commonXAxis}
                    yAxis={createdYAxis}
                    series={[{ data: totalCreated, label: "Lead Created", color: "rgba(255,0,0,0.8)", showMark: true }]}
                    width={chartWidth}
                    height={subChartHeight}
                    legend={{ hidden: true }}
                    sx={commonSx}
                />
                <Typography variant="caption" color="success.main" fontWeight={600} pl={1}>
                    Lead Won
                </Typography>
                <LineChart
                    xAxis={commonXAxis}
                    yAxis={wonYAxis}
                    series={[{ data: totalWon, label: "Lead Won", color: "#008000", showMark: true }]}
                    width={chartWidth}
                    height={subChartHeight}
                    legend={{ hidden: true }}
                    sx={commonSx}
                />
            </Box>
        </Box>
    );
};

export default VendorLeadTrendChart;